<template>
    <div>
      <b-card-header class="pb-50">
        <h5>
          Filtros
        </h5>
      </b-card-header>
      <b-card-body>
        <!--
          ATALHOS DE COMPETÊNCIA.

          Sem eles a tela abria com o período em branco, ou seja: com TUDO. E como a ordenação é
          por data, o topo da lista era ocupado por lançamentos de 2027, 2028 e um de 2926 —
          erros de digitação que ninguém corrige justamente porque ninguém acredita no que vê ali.

          Os atalhos escrevem no MESMO `rangeDate` do date-picker abaixo, de propósito: uma fonte
          da verdade só, sem modo "atalho" vs "personalizado" para manter em sincronia.

          ⚠️ O recorte é sobre `launch_date` (quando o valor cai numa fatura), NÃO sobre a
          competência de referência nem sobre `billing_date`. Por isso o rótulo diz o mês e não
          "competência 09/2026": prometer competência e filtrar data de lançamento é o tipo de
          divergência que já custou caro neste módulo.
        -->
        <b-row class="mb-1">
          <b-col cols="12" class="d-flex flex-wrap align-items-center">
            <small class="text-uppercase font-weight-bold mr-1">Período</small>
            <b-button
              v-for="shortcut in periodShortcuts"
              :key="shortcut.key"
              size="sm"
              class="mr-50 mb-50"
              :variant="shortcut.key === activePeriodKey ? 'primary' : 'outline-secondary'"
              :data-testid="`adjustments-period-shortcut-${shortcut.key}`"
              @click="applyPeriodShortcut(shortcut)"
            >
              {{ shortcut.label }}
            </b-button>
          </b-col>
        </b-row>
        <b-row>
          <b-col
            cols="12"
            md="6"
            class="mb-1"
          >
            <b-form-group
              label="PN"
              label-for="searchConsultant"
            >
              <v-select
                id="searchConsultant"
                multiple
                v-model="consultant"
                :reduce="consultant_list => consultant_list.id"
                :options="consultants"
                :loading="loading.consultants"
                label="name"
                @keyup.enter="search"
              >
                <template #no-options="{ search, searching, loading }">
                  Sem resultados
                </template>
              </v-select>
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="3"
            class="mb-1"
          >
            <b-form-group
              label="Forma de aplicação"
              label-for="searchCreditType"
            >
              <v-select
                id="searchCreditType"
                v-model="creditType"
                :reduce="credit_type => credit_type.key"
                :options="creditTypes"
                :loading="loading.creditTypes"
                label="name"
                @keyup.enter="search"
              >
                <template #no-options="{ search, searching, loading }">
                  Sem resultados
                </template>
              </v-select>
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="3"
            class="mb-1"
          >
            <b-form-group
              label="Motivo"
              label-for="searchAdjustmentReason"
            >
              <v-select
                id="searchAdjustmentReason"
                multiple
                v-model="adjustmentReason"
                :reduce="reasons => reasons.id"
                :options="adjustmentReasons"
                :loading="loading.adjustmentReasons"
                label="name"
                @keyup.enter="search"
              >
                <template #no-options="{ search, searching, loading }">
                  Sem resultados
                </template>
              </v-select>
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="3"
            class="mb-1"
          >
            <b-form-group
              label="Status"
              label-for="searchAdjustmentStatus"
            >
              <v-select
                id="searchAdjustmentStatus"
                multiple
                v-model="status"
                :reduce="statuses => statuses.value"
                :options="adjustmentStatus.statusOptions"
                label="name"
                @keyup.enter="search"
              >
                <template #no-options="{ search, searching, loading }">
                  Sem resultados
                </template>
              </v-select>
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="3"
            class="mb-1"
          >
            <b-form-group
              label="Empresa pagadora"
              label-for="searchPayingCompany"
            >
              <v-select
                id="searchPayingCompany"
                v-model="payingCompany"
                :reduce="option => option.id"
                :options="payingCompanyOptions"
                label="label"
                data-testid="adjustment-filter-paying-company"
                @keyup.enter="search"
              >
                <template #no-options="{ search, searching, loading }">
                  Sem resultados
                </template>
              </v-select>
              <small class="text-muted">
                "Sem empresa pagadora" traz os lançamentos anteriores à separação
                por CNPJ e a rolagem, que não pertencem a nenhum dos dois.
              </small>
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="3"
            class="mb-1"
          >
            <b-form-group
              label="Origem"
              label-for="searchOrigin"
            >
              <v-select
                id="searchOrigin"
                v-model="origin"
                :options="originOptions"
                :reduce="option => option.value"
                label="label"
                :clearable="false"
                data-testid="adjustments-origin-filter"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="3"
            class="mb-1"
          >
            <b-form-group
              label="Datas específicas"
              label-for="searchRangeDate"
            >
              <flat-pickr
                id="searchRangeDate"
                v-model="rangeDate"
                class="form-control"
                :config="flatPickrConfig"
                :placeholder="`01 Jan 2020 até 31 Dez ${actualYear}`"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-card-body>
    </div>
  </template>
  
  <script>
  import {
    BCard, BCardHeader, BCardBody, BRow, BCol, BFormGroup, BFormInput, BFormDatepicker,
    BFormRadioGroup, BFormRadio, BButton, BSpinner, BPopover, BCardText
  } from 'bootstrap-vue'
  import vSelect from 'vue-select'
  import { mapActions, mapGetters } from 'vuex'
  import { useToast } from 'vue-toastification/composition'
  import ToastificationContent from '@core/components/toastification/ToastificationContent'
  import ShortcutButtonsPlugin from 'shortcut-buttons-flatpickr'
  import todayButton from '@/helpers/todayButton'
  import * as types from '../store/types'
  import * as sharedTypes from '@/modules/shared/store/types'
  import * as accountTypes from '@/modules/account/store/types'
  import flatPickr from 'vue-flatpickr-component'
  import { Portuguese } from 'flatpickr/dist/l10n/pt.js';
  import * as adjustmentStatus from "@/constants/financial_adjustments";
import { FINANCIAL_ADJUSTMENT_APPROVAL, MANAGE_ACTION } from '@/constants/resources'
import { NONE_FILTER, PAYING_COMPANY_FILTER_OPTIONS } from '@/constants/paying_companies'
import { ORIGIN_ALL, ORIGIN_OPTIONS } from '@/constants/financial_adjustment_origins'

/**
 * Filtros que esta tela aceita pela query string, com os MESMOS nomes de
 * `FinancialAdjustmentController::searchRules()` — a URL é espelho 1:1 do corpo do
 * POST `/api/financial_adjustments/search`.
 *
 * ⚠️ `employee` e `department` existem em `searchRules` e são lidos pelo `search()` da view, mas
 * NÃO têm controle nesta tela (não estão em `data`): ficam de fora até ganharem select próprio.
 */
const ROUTE_FILTER_KEYS = [
  'consultant',
  'adjustment_reason',
  'status',
  'credit_type',
  'paying_company_id',
  'launch_date_start',
  'launch_date_end',
  'origin',
  // Recorte por operação em lote. Não tem controle nesta tela de propósito: chega só por link,
  // vindo do rastro no detalhe do ajuste. Um select de referências de lote seria a tela de lotes
  // de volta, pela porta dos fundos.
  'batch_reference',
  // Ajustes transferidos numa rolagem. Mesmo caso do lote: chega só pelo link do detalhe da
  // rolagem ou pelo badge da lista.
  'rolling_id',
]
  

  
  export default {
    components: {
      BRow,
      BCol,
      BCard,
      BCardHeader,
      BCardBody,
      BFormGroup,
      BFormInput,
      BFormDatepicker,
      BFormRadioGroup,
      BFormRadio,
      BButton,
      BSpinner,
      vSelect,
      BCardText,
      BPopover,
      flatPickr
    },
    props:{
      search: Function,
      spinnerActive: Boolean,
      periodType: String
    },
    data() {
      return {
        // Ligado quando a tela nasce de um link com filtros na URL. Serve só para o default
        // "pendente" não sobrescrever o que o link pediu — ver `mounted`.
        hasRouteFilters: false,
        adjustmentStatus,
        loading: {
          creditTypes: false,
          consultants: false,
          adjustmentReasons: false
        },
        flatPickrConfig:{
          altFormat: 'j M Y',
          altInput: true,
          mode: 'range',
          dateFormat: 'Y-m-d',
          locale: Portuguese,
          plugins:[
            ShortcutButtonsPlugin(todayButton)
          ]
        },
        creditType: undefined,
        consultant: undefined,
        adjustmentReason: undefined,
        rangeDate: undefined,
        status: undefined,
        payingCompany: undefined,
        origin: ORIGIN_ALL,
        dir: 'ltr'
      }
    },
    setup() {
      const actualYear = new Date().getFullYear()
      return { toast: useToast(), actualYear };
    },
    computed: {
      originOptions() {
        return ORIGIN_OPTIONS
      },
      /**
       * Os atalhos, sempre relativos a hoje.
       *
       * "Tudo" existe e é o único que devolve a lista inteira — a saída explícita para quem
       * precisa caçar um lançamento antigo, em vez de tirar o recorte pelo canto do date-picker.
       */
      periodShortcuts() {
        const today = new Date()
        const year = today.getFullYear()
        const month = today.getMonth()
        const previous = new Date(year, month - 1, 1)

        return [
          {
            key: 'current',
            label: `Este mês · ${this.monthLabel(today)}`,
            start: this.firstDayOf(year, month),
            end: this.lastDayOf(year, month),
          },
          {
            key: 'previous',
            label: `Mês passado · ${this.monthLabel(previous)}`,
            start: this.firstDayOf(previous.getFullYear(), previous.getMonth()),
            end: this.lastDayOf(previous.getFullYear(), previous.getMonth()),
          },
          {
            key: 'quarter',
            label: 'Últimos 3 meses',
            start: this.firstDayOf(year, month - 2),
            end: this.lastDayOf(year, month),
          },
          {
            key: 'year',
            label: String(year),
            start: this.firstDayOf(year, 0),
            end: this.lastDayOf(year, 11),
          },
          {
            key: 'all',
            label: 'Tudo',
            start: undefined,
            end: undefined,
          },
        ]
      },
      // O destaque sai do próprio `rangeDate`, e não de um estado paralelo: escolher a mesma
      // faixa pelo date-picker acende o atalho correspondente, como deve.
      activePeriodKey() {
        const shortcut = this.periodShortcuts.find(item => this.rangeDateFor(item) === this.rangeDate)
        return shortcut ? shortcut.key : undefined
      },
      ...mapGetters({
        consultants: sharedTypes.CONSULTANTS_PARTNER,
        creditTypes: sharedTypes.CREDIT_TYPES,
        adjustmentReasons: types.FINANCIAL_ADJUSTMENT_REASONS,
        initialItemsPerPage: sharedTypes.INITIAL_ITEMS_PER_PAGE,
        itemsPerPageOptions: sharedTypes.ITEMS_PER_PAGE_OPTIONS,
        user: accountTypes.USER
      }),
      canUpdateStatus() {
        return this.$can(MANAGE_ACTION, FINANCIAL_ADJUSTMENT_APPROVAL);
      },
      // "Sem empresa pagadora" não é lixo de migração que some depois: decidiu-se não
      // backfillar o histórico e a rolagem nunca recebe CNPJ, então `paying_company_id`
      // fica nullable para sempre e essa consulta é permanente.
      payingCompanyOptions() {
        return PAYING_COMPANY_FILTER_OPTIONS;
      },
    },
    /**
     * Filtros vindos da URL: `/ajustes-financeiros?paying_company_id=2&consultant=123`.
     *
     * Roda em `created` de propósito. Quem dispara a busca é o `mounted` do PAI
     * (`FinancialAdjustmentsList`), e o ciclo do Vue garante
     * created(filho) → mounted(filho) → mounted(pai): quando a busca sai, os filtros já estão
     * aplicados. A tela nasce filtrada E pesquisada, sem um segundo request.
     *
     * ⚠️ NÃO existe filtro por competência/fatura aqui nem em `searchRules`. O único recorte
     * temporal é `launch_date_*`, que é data de LANÇAMENTO — não a competência que faturou o
     * ajuste. Link vindo de uma nota fiscal não consegue recortar por competência.
     */
    created() {
      const query = this.$route.query || {}

      const consultant = this.queryToIdList(query.consultant)
      if (consultant) {
        this.consultant = consultant
      }

      const adjustmentReason = this.queryToIdList(query.adjustment_reason)
      if (adjustmentReason) {
        this.adjustmentReason = adjustmentReason
      }

      const status = this.queryToList(query.status)
      if (status) {
        this.status = status
      }

      if (query.credit_type) {
        this.creditType = query.credit_type
      }

      // ⚠️ 'none' (sem empresa pagadora) trafega como STRING no mesmo campo dos ids. Os ids
      // precisam voltar a ser Number: `:reduce="option => option.id"` guarda number, e uma string
      // "2" não casa com a opção — o v-select ficaria com o rótulo em branco mesmo filtrando certo.
      const payingCompanyId = query.paying_company_id
      if (payingCompanyId != null && payingCompanyId !== '') {
        this.payingCompany = String(payingCompanyId) === NONE_FILTER
          ? NONE_FILTER
          : Number(payingCompanyId)
      }

      // O flat-pickr guarda o intervalo como TEXTO ("2026-01-01 até 2026-01-31"); é esse texto que
      // `getRangeDates` volta a quebrar em start/end na view.
      const start = query.launch_date_start
      const end = query.launch_date_end
      if (start && end) {
        this.rangeDate = `${start} até ${end}`
      } else if (start) {
        this.rangeDate = start
      }

      if (query.origin) {
        this.origin = query.origin
      }

      this.hasRouteFilters = ROUTE_FILTER_KEYS
        .some(key => query[key] != null && query[key] !== '')

      // O DEFAULT QUE CONSERTA O "ABRE COM AJUSTES DE 2028".
      //
      // Só quando a tela nasce sozinha: chegando por link (memória de cálculo da nota, "ver os
      // lançamentos deste lote"), o recorte do link manda — e um default de período por cima
      // esconderia justamente o lançamento que o link existe para mostrar.
      if (!this.hasRouteFilters && !this.rangeDate) {
        this.applyPeriodShortcut(this.periodShortcuts[0])
      }
    },
    mounted() {
      this.loading.consultants = true
      this.getConsultants()
        .catch(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Oops!",
              text: "Ocorreu um erro ao carregar os consultores para seleção. Entre em contato com o setor de TI.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.loading.consultants = false
        })
      this.loading.creditTypes = true
      this.getCreditTypes()
        .catch(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Oops!",
              text: "Ocorreu um erro ao carregar os tipos de crédito para seleção. Entre em contato com o setor de TI.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.loading.creditTypes = false
        })
      this.loading.adjustmentReasons = true
      this.getAdjustmentReasons()
        .catch(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Oops!",
              text: "Ocorreu um erro ao carregar os motivos para seleção. Entre em contato com o setor de TI.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.loading.adjustmentReasons = false
        })
      // O default "pendente" SAIU daqui.
      //
      // Ele existia para pôr a fila de aprovação na frente de quem abre a tela — trabalho que
      // agora é do card "Aguardando aprovação", que mostra a fila inteira sem esconder nada.
      // Somados ao recorte de período, os dois defaults faziam a tela abrir duplamente filtrada,
      // e a queixa mais comum sobre esta lista era exatamente essa: aprovar um lançamento e vê-lo
      // sumir da tela sem explicação.
    },
    methods: {
      firstDayOf(year, month) {
        return this.toIsoDate(new Date(year, month, 1))
      },
      lastDayOf(year, month) {
        // Dia 0 do mês seguinte é o último dia deste — evita a tabela de 28/29/30/31.
        return this.toIsoDate(new Date(year, month + 1, 0))
      },
      // Formatação local, nunca `toISOString()`: ele converte para UTC e, em qualquer fuso a
      // oeste de Greenwich, o dia 1º vira o dia 30 do mês anterior. Já aconteceu neste repo.
      toIsoDate(date) {
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        return `${date.getFullYear()}-${month}-${day}`
      },
      monthLabel(date) {
        return `${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`
      },
      // O flat-pickr guarda o intervalo como TEXTO ("2026-09-01 até 2026-09-30"), e é esse texto
      // que a view quebra de volta em start/end. Os atalhos precisam falar a mesma língua.
      rangeDateFor(shortcut) {
        return shortcut.start && shortcut.end ? `${shortcut.start} até ${shortcut.end}` : undefined
      },
      applyPeriodShortcut(shortcut) {
        this.rangeDate = this.rangeDateFor(shortcut)
      },
      /**
       * Um valor só na query string chega como string; repetido, chega como array.
       *
       * ⚠️ O retorno é sempre ARRAY, mesmo com um id só: `scopeConsultant` e
       * `scopeAdjustmentReason` usam `whereIn`, que estoura recebendo escalar.
       */
      queryToIdList(value) {
        const list = this.queryToList(value)
        if (!list) return undefined
        const ids = list
          .map(item => parseInt(item, 10))
          .filter(id => !Number.isNaN(id))
        return ids.length ? ids : undefined
      },
      queryToList(value) {
        if (value == null || value === '') return undefined
        const list = (Array.isArray(value) ? value : [value])
          .filter(item => item != null && item !== '')
        return list.length ? list : undefined
      },
      ...mapActions({
        getConsultants: sharedTypes.GET_CONSULTANTS_PARTNER,
        getCreditTypes: sharedTypes.GET_CREDIT_TYPES,
        getAdjustmentReasons: types.GET_FINANCIAL_ADJUSTMENT_REASONS
      }),
    }
  }
  </script>
  
  <style lang="scss">
  $themeColor: #f26237;
  @import '~vue-slider-component/lib/theme/default.scss';
  @import '@core/scss/vue/libs/vue-flatpicker.scss';
  .form-group {
    margin-bottom: 0;
  }
  </style>
  